'use client';

import * as React from 'react';
import { useAuth } from '../auth';
import { cn } from '../utils/cn';
import { Button } from './button';
import { Card, CardContent } from './card';

export type LoginRegisterPanelProps = {
  /** Additional CSS classes */
  className?: string | undefined;
  /** Title to display */
  title?: string;
  /** Description text */
  description?: string;
};

/**
 * LoginRegisterPanel component that provides authentication UI.
 * Uses WorkOS AuthKit for secure OAuth authentication with integrated social providers.
 * AuthKit shows Google, Microsoft, and email/password options in a unified interface.
 * Follows the flat design system with no shadows or gradients.
 */
const LoginRegisterPanel = React.forwardRef<HTMLDivElement, LoginRegisterPanelProps>(
  ({ className, title = 'Welcome', description = 'Please sign in to continue' }, ref) => {
    const { login, isLoading } = useAuth();

    const handleSignIn = () => {
      // WorkOS AuthKit handles the complete authentication flow
      // including OAuth providers (Google, Microsoft), email verification, etc.
      // The AuthKit interface will show all configured social login options
      login();
    };

    // Determine button state
    const isButtonDisabled = isLoading;
    const buttonText = isLoading ? 'Opening sign in...' : 'Sign In';

    return (
      <Card ref={ref} className={cn('w-full max-w-md mx-auto', className)}>
        <CardContent className="p-8 space-y-6">
          <div className="text-center space-y-2">
            <h2 className="text-2xl font-semibold text-foreground">{title}</h2>
            <p className="text-muted-foreground">{description}</p>
          </div>

          <div className="space-y-4">
            <Button onClick={handleSignIn} disabled={isButtonDisabled} className="w-full" size="lg">
              {buttonText}
            </Button>
          </div>

          <div className="text-center">
            <p className="text-sm text-muted-foreground">
              By continuing, you agree to our Terms of Service and Privacy Policy
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }
);

LoginRegisterPanel.displayName = 'LoginRegisterPanel';

export { LoginRegisterPanel };
